import { BookingApiError, loadConfiguredAvailability } from './api';
import {
  BOOKING_TIMEZONE, BookingRange, BusySlot, RentalConfig, RentalOffer, getConfirmedAvailability,
  getNextBookingSlot, parseBusyTime,
} from './model';

export type NextAvailability =
  | { status: 'searching' }
  | { status: 'available'; range: BookingRange; expiresAt: string }
  | { status: 'none' }
  | { status: 'error' };

export interface NextAvailabilityRequest {
  offer: RentalOffer;
  range: BookingRange;
  vehicleIds: string[];
  accessoryIds: string[];
  busySlotsByVehicle?: Record<string, BusySlot[]>;
}

export function waitForAvailabilityRead(signal: AbortSignal, delay = 1500) {
  return new Promise<void>((resolve, reject) => {
    const abort = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
    const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve(); }, delay);
    if (signal.aborted) abort();
    else signal.addEventListener('abort', abort, { once: true });
  });
}

export async function findNextAvailableSlots(
  request: NextAvailabilityRequest, config: RentalConfig, signal: AbortSignal,
  onResult: (id: string, value: NextAvailability) => void,
  beforeRead: (signal: AbortSignal) => Promise<void> = waitForAvailabilityRead,
) {
  let pending = [...request.vehicleIds];
  let after = Date.parse(request.range.startsAt);
  const duration = (Date.parse(request.range.endsAt) - after) / 60_000;
  while (pending.length && !signal.aborted) {
    const slot = getNextBookingSlot(request.offer, config, Date.now(), after, duration);
    if (!slot) { pending.forEach(id => onResult(id, { status: 'none' })); return; }
    const start = Date.parse(slot.range.startsAt);
    const end = Date.parse(slot.range.endsAt);
    const skipUntil = new Map<string, number>();
    const availableIds = pending.filter(id => {
      for (const busy of request.busySlotsByVehicle?.[id] ?? []) {
        const busyStart = parseBusyTime(busy.fromDate, busy.fromHour)?.valueOf();
        const busyEnd = parseBusyTime(busy.toDate, busy.toHour)?.valueOf();
        if (busyStart === undefined || busyEnd === undefined || busyEnd <= busyStart) {
          throw new BookingApiError('INVALID_RESPONSE');
        }
        if (start < busyEnd && end > busyStart) {
          skipUntil.set(id, (start < busyStart ? busyStart : busyEnd) - 1);
          return false;
        }
      }
      return true;
    });
    if (!availableIds.length) {
      after = Math.min(...Array.from(skipUntil.values()));
      continue;
    }
    await beforeRead(signal);
    const snapshot = await loadConfiguredAvailability({
      offerId: request.offer, ...slot.range, timezone: BOOKING_TIMEZONE,
      vehicleIds: availableIds, accessoryIds: request.accessoryIds,
    }, config, signal);
    if (signal.aborted) return;
    const now = Date.now();
    const accessories = request.accessoryIds.map(id => getConfirmedAvailability(snapshot, 'accessories', id, now));
    if (accessories.some(status => status !== 'available' && status !== 'unavailable')) {
      throw new BookingApiError('AVAILABILITY_UNKNOWN');
    }
    pending = pending.filter(id => {
      if (!availableIds.includes(id)) return true;
      const status = getConfirmedAvailability(snapshot, 'vehicles', id, now);
      if (status === 'unknown') { onResult(id, { status: 'error' }); return false; }
      if (status === 'available' && accessories.every(status => status === 'available')) {
        onResult(id, { status: 'available', range: slot.range, expiresAt: snapshot.expiresAt }); return false;
      }
      return true;
    });
    after = Date.parse(slot.range.startsAt);
  }
}
